import type { MonospaceWorkspace } from '#layers/data-model/public';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuth } from '#layers/access/stores/auth';
import { useSystemEntityStore } from '#layers/runtime/public';

/**
 * The workspaces the current user can access, resolved through their memberships. A non-admin
 * member can read neither the workspace list nor the users endpoint (both need an org
 * entitlement), so the memberships ride along on the auth store's `/me` fetch and are read back
 * out of the cache here — `cache-only`, because fetching them would be the 403.
 */
export function useMemberWorkspaces() {
	const store = useSystemEntityStore();
	const { currentUser } = storeToRefs(useAuth());

	const currentUserId = computed(() => currentUser.value?.id ?? null);

	const userQuery = store.MonospaceUser.query((q) => currentUserId.value
		? q.first({
				key: currentUserId.value,
				include: { workspaces: { include: { workspace: {} } } },
				fetchPolicy: 'cache-only',
			})
		: q.first({ enabled: false }));

	const memberWorkspaces = computed<MonospaceWorkspace[]>(() =>
		(userQuery.data.value?.workspaces ?? [])
			.flatMap((member) => (member.workspace ? [member.workspace] : [])));

	return { memberWorkspaces, isLoading: userQuery.loading };
}
